import { tsvClipboardSourceAdapter } from 'helpers/tableClipboard.helper/tableClipboard.helper'
import type { RefObject } from 'react'
import { useEffect, useLayoutEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import alertStore from 'store/alert/alert'
import { parseElplanCellKey } from './cells.adapter'
import type { ElplanDraft, ElplanSlot, ElplanSlotEdit, ElplanSlotUnit } from './ElplanEditor.types'
import { mapElplanPastePayload } from './paste'

type UseElplanEditorPasteOptions = {
  tableContainerRef: RefObject<HTMLDivElement | null>
  selectedCellKeysRef: RefObject<ReadonlySet<string>>
  draft: ElplanDraft
  mode: ElplanSlotUnit
  onEdits: (edits: ElplanSlotEdit[]) => void
  getEditTargets: (cellKey: string) => ReadonlySet<string>
  replaceSelection: (nextSelection: Set<string>) => void
  setAnchorCellKey: (cellKey: string | null) => void
  onEmptyPaste: (cellKey: string) => void
}

function getPastedCellKey(event: ClipboardEvent): string | null {
  const cell = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-cell-key]') : null
  return cell?.dataset.cellKey ?? null
}

function toSlots(cellKeys: Iterable<string>): ElplanSlot[] {
  return [...cellKeys].flatMap((cellKey) => parseElplanCellKey(cellKey) ?? [])
}

export function useElplanEditorPaste(options: UseElplanEditorPasteOptions): void {
  const { t } = useTranslation()
  const optionsRef = useRef(options)
  useLayoutEffect(() => {
    optionsRef.current = options
  })
  const { tableContainerRef } = options

  useEffect(() => {
    const tableContainer = tableContainerRef.current
    if (!tableContainer) {
      return
    }

    function handlePaste(event: ClipboardEvent): void {
      event.preventDefault()
      const {
        selectedCellKeysRef,
        draft,
        mode,
        onEdits,
        getEditTargets,
        replaceSelection,
        setAnchorCellKey,
        onEmptyPaste,
      } = optionsRef.current
      const fieldKey = getPastedCellKey(event)
      if (fieldKey === null) {
        return
      }

      const payload = tsvClipboardSourceAdapter.toClipboardPayload(event.clipboardData?.getData('text/plain') ?? '')
      const selectedCellKeys = selectedCellKeysRef.current
      const selectedSlots = toSlots(selectedCellKeys.size > 0 ? selectedCellKeys : [fieldKey])
      const edits = mapElplanPastePayload(payload, selectedSlots, toSlots(getEditTargets(fieldKey)), mode, draft)
      if (edits === null) {
        alertStore.error(t('Nothing was pasted. Copy one column and paste again.'))
        return
      }
      if (edits.length === 0) {
        onEmptyPaste(fieldKey)
        return
      }

      onEdits(edits)
      if (payload.length > 1) {
        replaceSelection(new Set([fieldKey]))
        setAnchorCellKey(fieldKey)
      }
    }

    function handleBeforeInput(event: InputEvent): void {
      if (event.inputType === 'insertFromDrop') {
        event.preventDefault()
      }
    }

    function handleDragStart(event: DragEvent): void {
      event.preventDefault()
    }

    tableContainer.addEventListener('paste', handlePaste)
    tableContainer.addEventListener('beforeinput', handleBeforeInput)
    tableContainer.addEventListener('dragstart', handleDragStart)
    return () => {
      tableContainer.removeEventListener('paste', handlePaste)
      tableContainer.removeEventListener('beforeinput', handleBeforeInput)
      tableContainer.removeEventListener('dragstart', handleDragStart)
    }
  }, [t, tableContainerRef])
}
